import React, { useState, useEffect } from 'react';
import './home.css';

function IndustrySignals({ signals, onNavigateToJobs }) {
  return (
    <div className="home-industry-signals">
      {signals.slice(0, 6).map((s) => (
        <button
          key={s.industry}
          className="home-signal-row"
          onClick={() => onNavigateToJobs && onNavigateToJobs({ industries: [s.industry] })}
        >
          <div className="home-signal-top">
            <span className="home-signal-name">{s.industry}</span>
            <span className={`home-signal-ticker home-signal-ticker-${s.direction || 'neutral'}`}>
              {s.direction === 'up' ? '▲' : s.direction === 'down' ? '▼' : ''}
              {s.tickerLabel} {Math.abs(s.change_pct).toFixed(1)}%
            </span>
          </div>
          {s.topCompany && (
            <div className="home-signal-company">
              Most hiring: {s.topCompany} ({s.topCompanyCount} roles)
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

// ─── Home Page Signals ────────────────────────────────────────────────────────
// Single quote:  { id, quote, source, sourceUrl, linkText, filterIndustries, paperLink, externalLink }
// Multi-line:    replace quote/source/sourceUrl with lines: [{ text, source, sourceUrl }, ...]
// paperLink: { text, section }           — navigates to /paper/:section
//            { text, section, article }  — navigates to /paper/:section/:article
// externalLink: { text, url }            — opens external URL in new tab
// image: path to image in public folder   — shown below quote, above links
const SIGNALS = [
  {
    id: 7,
    quote: "Saudi Aramco's East-West pipeline to hit full capacity in 'next couple of days:' says CEO",
    source: "Barron's",
    sourceUrl: 'https://www.barrons.com/articles/saudi-aramco-oil-prices-pipeline-hormuz-iran-395c4db2',
    image: '/aramco-east-west-pipeline-map.png',
    imageAlt: 'Map of Saudi Arabia East-West crude oil pipeline and Strait of Hormuz',
    paperLink: { text: 'Read about their $100B East-West pipeline here', section: 'energy', article: 'aramco-east-west-pipeline-hormuz-barrons' },
    externalLink: { text: 'Check out open jobs for US Expats at Aramco', url: 'https://careers.aramco.com/expat_us/go/For-US-Applicants/7717823/?q=&sortColumn=referencedate&sortDirection=desc' },
  },
  {
    id: 6,
    quote: 'Cadillac paid a $450 million "dilution fee" to existing F1 teams just to be allowed to compete — before a single car was built. Total investment: over $1 billion. They received 143,000 job applications for 595 open roles.',
    source: 'Joshua Robinson at The Wall Street Journal',
    sourceUrl: 'https://www.wsj.com/sports/cadillac-f1-team-3b85cdbb',
    linkText: 'Open roles in automotive & motorsport engineering →',
    filters: { industries: ['Automotive & Motorsport'] },
    paperLink: { text: 'Read more in the Paper →', section: '24', article: 'cadillac-f1' },
  },
  {
    id: 5,
    quote: 'A Vancouver startup just raised $5.7M to prevent wildfires before they start — by seeding clouds to neutralize lightning strikes mid-storm. Skyward Wildfire is the world\'s first lightning prevention company, and wildfire season is only getting longer.',
    source: 'Skyward Wildfire — MIT Technology Review',
    sourceUrl: 'https://www.technologyreview.com/2026/03/03/1133848/this-startup-claims-it-can-stop-lightning-and-prevent-catastrophic-wildfires/',
    linkText: 'Open roles at climate & clean tech companies →',
    filters: { industries: ['Energy & Climate'] },
    paperLink: { text: 'Read about it in the Paper →', section: 'energy', article: 'skyward-wildfire-lightning' },
  },
  {
    id: 4,
    quote: 'If these drugs turn out to be safe and efficacious for treatment of substance-use disorder, because they are so broadly used for other reasons in our society, they would just automatically, de facto, become the most widely prescribed pharmacotherapy for addiction.',
    source: 'Dr. W. Kyle Simmons, Oklahoma State University — CNN Health',
    sourceUrl: 'https://www.cnn.com/2026/03/04/health/glp1-addiction-treatment',
    linkText: 'These pharma companies are hiring →',
    filters: { industries: ['Biotechnology', 'Healthcare'] },
    paperLink: { text: 'Read more in the Paper →', section: 'meds', article: 'glp1-addiction' },
  },
  {
    id: 3,
    lines: [
      {
        text: 'Defense stocks often rise at the onset of fighting and retract as conflicts stabilize. The rapid success of U.S. forces suggests a potentially quicker end to the conflict, leading to a Wednesday morning slide: Northrop Grumman (NOC): ↓ 2%, Lockheed Martin (LMT): ↓ 1.3%, L3Harris (LHX): ↓ 1.1%, RTX: ↓ 0.3%.',
        source: "Barron's",
        sourceUrl: 'https://www.barrons.com/articles/northrop-stock-price-defense-general-dynamics-82187c5e',
      },
      {
        text: 'The iShares U.S. Aerospace & Defense ETF is up 60% over the last 12 months.',
        source: 'George Glover',
        sourceUrl: 'https://www.ishares.com/us/products/239737/ISHARES-US-AEROSPACE-DEFENSE-ETF',
      },
    ],
    linkText: 'iShares U.S. Aerospace & Defense ETF jobs →',
    filters: { industries: ['Aerospace & Defense'] },
    paperLink: { text: 'Read more in the Paper →', section: 'bored', article: 'defense-dip' },
  },
  {
    id: 2,
    lines: [
      {
        text: 'Trump says strikes on Iran could take weeks...',
        source: 'The Post',
        sourceUrl: 'https://www.washingtonpost.com/world/2026/03/02/us-israel-iran-hezbollah-military-strikes-live-updates/#link-T3I7FJNDIBBT5NTB46V3XJT54Y',
      },
      {
        text: 'These Defense Stocks Are Today\'s Movers — Northrop Grumman (+6%), Palantir (+5.8%), RTX/Raytheon (+4.7%), L3Harris (+3.8%), Lockheed Martin (+3.4%).',
        source: "Barron's",
        sourceUrl: 'https://www.barrons.com/articles/stock-movers-b961ba1f?mod=stockoverview',
      },
    ],
    linkText: 'Open roles at defense contractors →',
    filters: { industries: ['Aerospace & Defense'] },
    paperLink: { text: 'Read more in the Paper →', section: 'bored' },
  },
  {
    id: 1,
    quote: 'Total global hedge fund industry assets hit a historic $5 trillion milestone for the first time last year, with net asset inflows of $116 billion, the strongest calendar year for investor inflows since 2007.',
    source: "Barron's",
    sourceUrl: 'https://www.barrons.com/articles/alternative-investments-funds-b489b84e?mod=hp_SP_A_3_1',
    linkText: 'Open roles at hedge funds →',
    filters: { industries: ['Finance'] },
    paperLink: { text: 'Read more in the Paper →', section: 'fish' },
  },
];
// ─────────────────────────────────────────────────────────────────────────────

function HomePage({ onNavigateToJobs, onNavigateToPaper }) {
  const [signals, setSignals] = useState([]);

  useEffect(() => {
    fetch('/api/industry-signals')
      .then(r => r.json())
      .then(d => setSignals(d.signals || []))
      .catch(() => {});
  }, []);

  return (
    <div className="home-page">
      <IndustrySignals signals={signals} onNavigateToJobs={onNavigateToJobs} />
      <main className="home-feed">
        {SIGNALS.map((signal) => {
          const topSource = signal.lines ? signal.lines[0] : signal;
          return (
          <article key={signal.id} className="home-signal">
            <div className="home-signal-source">
              {topSource.sourceUrl ? (
                <a href={topSource.sourceUrl} target="_blank" rel="noopener noreferrer">
                  {topSource.source.toUpperCase()}
                </a>
              ) : topSource.source.toUpperCase()}
            </div>
            <blockquote className="home-quote">
              {signal.lines ? (
                signal.lines.map((line, i) => (
                  <p key={i} className={i > 0 ? 'home-quote-line' : ''}>
                    "{line.text}"
                  </p>
                ))
              ) : (
                <p>"{signal.quote}"</p>
              )}
            </blockquote>
            {signal.image && (
              <div className="home-signal-image">
                <img src={signal.image} alt={signal.imageAlt || ''} />
              </div>
            )}
            <div className="home-signal-links">
              {signal.filters && (
                <button
                  className="home-jobs-link"
                  onClick={() => onNavigateToJobs(signal.filters)}
                >
                  {signal.linkText}
                </button>
              )}
              {signal.paperLink && (
                <button
                  className="home-paper-link"
                  onClick={() => onNavigateToPaper(
                    signal.paperLink.article
                      ? `${signal.paperLink.section}/${signal.paperLink.article}`
                      : signal.paperLink.section
                  )}
                >
                  {signal.paperLink.text}
                </button>
              )}
              {signal.externalLink && (
                <a
                  className="home-paper-link"
                  href={signal.externalLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {signal.externalLink.text}
                </a>
              )}
            </div>
          </article>
        )})}
      </main>
    </div>
  );
}

export default HomePage;
